import { Body, Box, Button, Flex, Image, Input, Link } from '@hover/blueprint';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '@hover/blueprint/chakra';
import React, { useContext, useEffect, useState } from 'react';

import {
  DOOR_TEXTURE_TOLERANCES,
  StyleButton,
} from 'src/modules/designStudio/components/DesignMenu/Menu/DoorGroupMenu/DoorGroupDetail';
import type {
  DesignBuilding,
  DoorTexture,
} from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import { DoorGroup } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import { MacheteBuildingContext } from 'src/modules/designStudio/providers';
import type {
  DoorCatalog,
  DoorStyle,
  DoorVariation,
} from 'src/modules/designStudio/providers/ProductCatalogProvider/doors/types';

const text = {
  title: 'Door Catalog Debug Menu',
  apply: 'Apply',
};

export const createURIForVariation = (variation: DoorVariation) =>
  encodeURI(
    `https://products.hover.to/admin/variation?f[uuid][22428][v]=${variation.uuid}&query/`,
  );

export const createURIForProduct = (product: DoorStyle) =>
  encodeURI(
    `https://products.hover.to/admin/product?f[uuid][22428][v]=${product.uuid}&query/`,
  );

export type ProductsJSON = {
  name: string;
  id: number;
  texture: { thumbnail: { url: string }; diffuse_map: { url: string } };
}[];
const boxShadow = 'rgba(0, 0, 0, 0.16) 0px 1px 4px;';

export const debugUpdateDoorTexture = (
  building: DesignBuilding | undefined,
  group: DoorGroup,
  variation: DoorVariation,
  color: string | undefined,
  tolerance: number,
) => {
  const design = building!.designer.get_active_design();

  const doorTextures: DoorTexture[] = [variation].map(v => ({
    textureId: v.textureId,
    url: v.url,
    width: v.width,
    height: v.height,
    canTile: v.canTile,
  }));

  switch (group) {
    case DoorGroup.FRONT:
      void design.change_front_doors(doorTextures, color, tolerance);
      break;
    case DoorGroup.SERVICE:
      void design.change_service_doors(doorTextures, color, tolerance);
      break;
    case DoorGroup.GARAGE:
      void design.change_garage_doors(doorTextures, color, tolerance);
      break;
    default:
      break;
  }
};

const DoorVariationComp = ({
  doorType,
  variation,
  productsJSON,
}: {
  doorType: DoorGroup;
  variation: DoorVariation;
  productsJSON: ProductsJSON;
}) => {
  const { building } = useContext(MacheteBuildingContext);
  const [color, setColor] = useState('#ffffff');
  const p = productsJSON.find(product => product.id === variation.textureId);
  const imageURL = p ? p.texture.diffuse_map.url : '';

  return (
    <Flex
      borderColor="blue.500"
      borderRadius="10px"
      borderStyle="solid"
      borderWidth="2px"
      direction="column"
      gap="8px"
      padding="8px"
    >
      <Link href={createURIForVariation(variation)}>
        {`${variation.styleName}`}
      </Link>
      <Body fontSize={100}>{`UUID: ${variation.uuid}`}</Body>
      <Body>{`${variation.width} x ${variation.height}`}</Body>
      <Body>{`W/H = ${(variation.width / variation.height).toFixed(3)}`}</Body>
      <Box
        bg={variation.color ? variation.color : '#ffffff'}
        borderRadius="10px"
        height="25px"
        width="100%"
      />
      <Image alt="door-image" height="200px" src={imageURL} width="100px" />
      {!variation.color && (
        <Input
          onChange={val => {
            setColor(val.currentTarget.value);
          }}
          type="color"
          value={color}
        />
      )}
      <Button
        onClick={() => {
          debugUpdateDoorTexture(
            building,
            doorType,
            variation,
            color,
            Infinity,
          );
        }}
      >
        {`Force ${text.apply}`}
      </Button>
      <Button
        onClick={() => {
          debugUpdateDoorTexture(
            building,
            doorType,
            variation,
            color,
            DOOR_TEXTURE_TOLERANCES[doorType],
          );
        }}
      >
        {`${text.apply}`}
      </Button>
    </Flex>
  );
};

const DoorStyleComp = ({
  doorType,
  style,
  json,
}: {
  doorType: DoorGroup;
  style: DoorStyle;
  json: ProductsJSON;
}) => (
  <Flex
    borderRadius="10px"
    boxShadow={boxShadow}
    direction="column"
    gap="8px"
    padding="8px"
  >
    <Accordion allowToggle>
      <AccordionItem>
        <Link
          fontSize={400}
          href={createURIForProduct(style)}
          my="8px"
          textAlign="center"
        >
          {`${style.name} (${style.variations.length})`}
        </Link>
        <AccordionButton>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel>
          <Body fontSize={100}>{`UUID: ${style.uuid}`}</Body>
          <StyleButton
            icon={style.icon}
            isSelected={false}
            key={`door-style-button-${style.name}`}
            onClick={() => {}}
            styleName={style.name}
          />
          <Body
            fontSize={400}
            my="8px"
          >{`${style.variations.length} Variations`}</Body>
          <Flex direction="row" flexWrap="nowrap" gap="8px" overflowX="scroll">
            {style.variations.map(v => (
              <DoorVariationComp
                doorType={doorType}
                productsJSON={json}
                variation={v}
              />
            ))}
          </Flex>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  </Flex>
);

/**
 * @description This component is only meant for local development purposes, it allows you to view
 * and apply styles in the door catalog.
 * @param doorType
 * @param catalog
 * @constructor
 */
export const DebugDoorGroup = ({
  doorType,
  catalog,
}: {
  doorType: DoorGroup;
  catalog: DoorCatalog;
}) => {
  const [products, setProducts] = useState<undefined | ProductsJSON>(undefined);

  useEffect(() => {
    const fetchData = async () => {
      /* eslint-disable */
      const productsJSON = await (
        await fetch('https://products.hover.to/hover_models/products.json')
      ).json();
      /* eslint-enable */
      setProducts(productsJSON as ProductsJSON);
    };
    void fetchData();
  }, []);

  if (!products) {
    return null;
  }

  return (
    <>
      <Body fontWeight={700}>{text.title}</Body>
      <Flex direction="column" gap="8px">
        <Body
          fontWeight={700}
        >{`${catalog.stylesByDoorType[doorType].styles.length} ${doorType} Door Products`}</Body>
        {catalog.stylesByDoorType[doorType].styles.map(style => (
          <DoorStyleComp doorType={doorType} json={products} style={style} />
        ))}
      </Flex>
    </>
  );
};
